import React, { useEffect, useState, useMemo, useRef, useCallback, memo } from "react"
import {
    Alert,
    Card,
    CardBody,
    Col,
    Row,
} from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import {
    getOfferTags,
    manageOfferTags,
    clearOfferTags,
    getOfferTagDefinitions,
    clearOfferTagDefinitions,
} from "store/offers/actions"
import * as url from "../../helpers/url_helper";
import ConfirmButton from "components/Common/ConfirmButton"
import CButton from "components/Common/CButton"
import SimpleTableContainer from "components/Common/SimpleTableContainer"
import AccessControlWrapper from "components/Common/PermissionButton"

// Memoized tag checkbox - only re-renders when its own checked value changes
const TagCheckbox = memo(({ tag, checked, onToggle, getTagIcon }) => (
    <div className="form-check form-check-primary">
        <input
            type="checkbox"
            className="form-check-input"
            id={`tag_${tag.tagID}`}
            checked={checked}
            onChange={() => {}}
            onClick={() => onToggle(tag.tagID)}
        />
        <label className="form-check-label" htmlFor={`tag_${tag.tagID}`}>
            <div className="d-flex">
                {getTagIcon(tag)}
                <div>
                    <div className="fw-medium">{tag.tag}</div>
                    {tag.description && (
                        <small className="text-muted">{tag.description}</small>
                    )}
                </div>
            </div>
        </label>
    </div>
))
TagCheckbox.displayName = "TagCheckbox"

export default function OfferTagsForm({
    page,
    match,
    onTabCompletion,
    activeTab,
    tabId,
    createdOfferID,
    offerType,
    canEdit,
    copyFromID,
    fromType,
}) {
    const [showEditMode, setShowEditMode] = useState(page === "add" && canEdit)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [selectedTagIDs, setSelectedTagIDs] = useState([])
    const [previousTagsLength, setPreviousTagsLength] = useState(0)

    const prevTagManagementSuccess = useRef(null)
    const prevTagError = useRef(null)
    const hasFetchedCopyData = useRef(false)

    const dispatch = useDispatch()
    const detailID = match?.params?.id

    const offerID = page === "add" ? createdOfferID : detailID

    // For copy functionality, use the source offer ID to load tag data initially,
    // but switch to createdOfferID once the offer is created
    const sourceOfferID = (copyFromID && page === "add" && !createdOfferID) ? copyFromID : offerID

    const {
        offerTags,
        tagError,
        isLoadingTags,
        offerTagDefinitions,
        isLoadingDefinitions,
        definitionError,
        tagManagementSuccess,
    } = useSelector(state => ({
        offerTags: state.offers.offerTags || [],
        tagError: state.offers.tagError,
        isLoadingTags: state.offers.isLoadingTags || false,
        offerTagDefinitions: state.offers.offerTagDefinitions || [],
        isLoadingDefinitions: state.offers.isLoadingDefinitions || false,
        definitionError: state.offers.definitionError,
        tagManagementSuccess: state.offers.tagManagementSuccess,
    }))

    // Helper function to check if error should be displayed
    const shouldDisplayError = error => {
        if (!error) return false

        const errorMessage = error.message || error.description || ""
        const errorCode = error.code || error.status || ""

        const noDataPatterns = ["no content available"]

        return !noDataPatterns.some(
            pattern =>
                errorMessage.toLowerCase().includes(pattern) ||
                errorCode.toString().includes("404")
        )
    }

    // Pre-fetch offer tags when copying (even if tab is not active yet)
    useEffect(() => {
        if (copyFromID && page === "add" && !createdOfferID && !hasFetchedCopyData.current) {
            hasFetchedCopyData.current = true
            dispatch(
                getOfferTags({
                    headers: {
                        "input-parameters": { offerID: copyFromID },
                    },
                })
            )
        }
    }, [dispatch, copyFromID, page, createdOfferID])

    // Fetch offer tags when tab is active (for detail mode or after save)
    useEffect(() => {
        const isTabActive = activeTab === tabId

        if (sourceOfferID && isTabActive && (page === "detail" || (page === "add" && tagManagementSuccess))) {
            dispatch(
                getOfferTags({
                    headers: {
                        "input-parameters": { offerID: sourceOfferID },
                    },
                })
            )
        }
    }, [dispatch, sourceOfferID, page, tagManagementSuccess, activeTab, tabId])

    useEffect(() => {
        if (showEditMode || page === "add") {
            dispatch(
                getOfferTagDefinitions({
                    headers: {
                        "input-parameters": {},
                    },
                })
            )
        }
    }, [dispatch, showEditMode, page])

    // Initialize selected tags when offer tags are loaded
    useEffect(() => {
        if (offerTags.length > 0 && showEditMode && (page === "detail" || (page === "add" && copyFromID))) {
            const tagIDs = offerTags.map(tag => String(tag.offerTag?.tagID || tag.tagID))
            setSelectedTagIDs(tagIDs)
        }
    }, [offerTags, showEditMode, page, copyFromID])

    useEffect(() => {
        // Clear offer tags data when in add mode without copy to prevent showing stale data
        if (page === "add" && !copyFromID) {
            dispatch(clearOfferTags())
            setSelectedTagIDs([])
        }
    }, [dispatch, page, copyFromID])

    useEffect(() => {
        return () => {
            dispatch(clearOfferTags())
            dispatch(clearOfferTagDefinitions())
        }
    }, [dispatch])

    // Handle success states
    useEffect(() => {
        if (tagManagementSuccess && tagManagementSuccess !== prevTagManagementSuccess.current) {
            setIsSubmitting(false)
            setShowEditMode(false)
            hasFetchedCopyData.current = false

            if (page === "add" && onTabCompletion) {
                onTabCompletion("2")
            }

            prevTagManagementSuccess.current = tagManagementSuccess
        }
    }, [tagManagementSuccess, page, onTabCompletion])

    // Separate effect for error handling
    useEffect(() => {
        if (tagError && tagError !== prevTagError.current) {
            setIsSubmitting(false)
            prevTagError.current = tagError
        }
    }, [tagError])

    // Track tags length changes
    useEffect(() => {
        if (offerTags.length > previousTagsLength) {
            setPreviousTagsLength(offerTags.length)

            if (page === "add" && tagManagementSuccess && offerTags.length > 0) {
                setShowEditMode(false)
            }
        }
    }, [offerTags.length, previousTagsLength, page, tagManagementSuccess])

    // Handle tag checkbox toggle
    const handleTagToggle = useCallback((tagID) => {
        const id = String(tagID)
        setSelectedTagIDs(prev =>
            prev.includes(id)
                ? prev.filter(x => x !== id)
                : [...prev, id]
        )
    }, [])

    // Handle save tags
    const handleSaveTags = async () => {
        if (!offerID || selectedTagIDs.length === 0) return

        setIsSubmitting(true)

        const payload = {
            actionType: "create",
            offerID: parseInt(offerID),
            tagIDs: selectedTagIDs,
        }

        try {
            dispatch(manageOfferTags(payload, null))
        } catch (error) {
            setIsSubmitting(false)
            console.error("Error saving tags:", error)
        }
    }

    // Handle edit mode toggle
    const handleEditMode = () => {
        if (!canEdit) {
            console.warn("Edit mode not allowed for this offer type")
            return
        }
        setShowEditMode(true)
        const tagIDs = offerTags.map(tag => String(tag.offerTag?.tagID || tag.tagID))
        setSelectedTagIDs(tagIDs)
    }

    // Handle cancel edit
    const handleCancelEdit = () => {
        setShowEditMode(false)
        setSelectedTagIDs([])
    }

    const groupedTagDefinitions = useMemo(() => {
        const groups = {}
        offerTagDefinitions.forEach(tag => {
            const categoryName = tag.tagCategory?.category || "Uncategorized"
            if (!groups[categoryName]) {
                groups[categoryName] = []
            }
            groups[categoryName].push(tag)
        })

        return groups
    }, [offerTagDefinitions])

    // Get tag icon
    const getTagIcon = tag => {
        if (tag.icon) {
            return (
                <img
                    src={tag.icon}
                    alt={tag.tag}
                    style={{ width: "20px", height: "20px", marginRight: "8px", filter: "invert(1)" }}
                />
            )
        }
        return <i className="bx bx-tag me-2"></i>
    }

    const renderTagSelectionForm = () => (
        <Card className="mb-4">
            <CardBody>
                <h6 className="mb-3">Select Tags</h6>
                {Object.keys(groupedTagDefinitions).map(categoryName => (
                    <div key={categoryName} className="mb-4">
                        <h6 className="text-primary mb-3">{categoryName}</h6>
                        <Row>
                            {groupedTagDefinitions[categoryName].map(tag => (
                                <Col md="6" lg="4" key={tag.tagID} className="mb-2">
                                    <TagCheckbox
                                        tag={tag}
                                        checked={selectedTagIDs.includes(String(tag.tagID))}
                                        onToggle={handleTagToggle}
                                        getTagIcon={getTagIcon}
                                    />
                                </Col>
                            ))}
                        </Row>
                    </div>
                ))}

                {selectedTagIDs.length === 0 && (
                    <div className="text-danger mb-3">Please select at least one tag</div>
                )}

                {/* Action buttons */}
                <div className="text-end">
                    {page === "detail" && (
                        <CButton.Secondary
                            text="Cancel"
                            className="me-2"
                            onClick={handleCancelEdit}
                            disabled={isSubmitting}
                        />
                    )}
                    <ConfirmButton
                        contextUri={url.MANAGE_OFFER_TAGS}
                        actionType="post"
                        validateBeforeConfirm={() => selectedTagIDs.length > 0}
                        onConfirm={() => handleSaveTags()}
                        message="Are you sure you want to save these tags?"
                        disabled={isSubmitting || selectedTagIDs.length === 0}
                        renderButton={
                            <CButton.Primary
                                text="Save Tags"
                                loading={isSubmitting}
                                disabled={isSubmitting || selectedTagIDs.length === 0}
                            />
                        }
                    />
                </div>
            </CardBody>
        </Card>
    )

    return (
        <>
            {/* Error Display */}
            {tagError && shouldDisplayError(tagError) && (
                <Alert color="danger" className="mb-3">
                    {tagError.message || "An error occurred"}
                </Alert>
            )}

            {definitionError && shouldDisplayError(definitionError) && (
                <Alert color="warning" className="mb-3">
                    Unable to load tag definitions: {definitionError.message || "Please try again"}
                </Alert>
            )}

            {/* Tag Selection Form - Show in add mode or edit mode */}
            {showEditMode && renderTagSelectionForm()}

            {!showEditMode && (page === "detail" || (page === "add" && offerTags.length > 0)) && (
                <SimpleTableContainer
                    title="Tags"
                    data={offerTags
                        .map((tag, index) => ({
                            ...tag,
                            id: index,
                        }))}
                    columns={[
                        {
                            key: "icon",
                            label: "Icon",
                            width: "60px",
                            align: "center",
                            render: tag => getTagIcon(tag.offerTag || tag),
                        },
                        {
                            key: "tag",
                            label: "Tag",
                            render: tag => tag.offerTag?.tag || tag.tag || "---",
                        },
                        {
                            key: "description",
                            label: "Description",
                            render: tag => tag.offerTag?.description || tag.description || "---",
                        },
                        {
                            key: "category",
                            label: "Category",
                            render: tag => tag.offerTag?.tagCategory?.category || tag.tagCategory?.category || "---",
                        },
                    ]}
                    loading={isLoadingTags}
                    emptyMessage="No tags assigned yet"
                    emptyIcon="bx bx-tag"
                    showActions={false}
                    headerActions={
                        !canEdit ? null : (
                            offerTags.length === 0 ? (
                                <AccessControlWrapper
                                    contextUri={url.MANAGE_OFFER_TAGS}
                                    actionType="post"
                                >
                                    <CButton.Success
                                        icon="bx bx-plus"
                                        text="Add Tags"
                                        onClick={handleEditMode}
                                        disabled={isLoadingDefinitions}
                                    />
                                </AccessControlWrapper>
                            ) : (
                                <AccessControlWrapper
                                    contextUri={url.MANAGE_OFFER_TAGS}
                                    actionType="post"
                                >
                                    <CButton.Primary
                                        text="Edit Tags"
                                        onClick={handleEditMode}
                                        disabled={isLoadingDefinitions}
                                    />
                                </AccessControlWrapper>
                            )
                        )
                    }
                />
            )}

        </>
    )
}
